import { Component, type ReactNode } from "react";
import { Box, Button, Typography } from "@mui/material";

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  isChunkError: boolean;
  shouldAutoReload: boolean;
}

function isChunkLoadError(error: unknown): boolean {
  if (!(error instanceof Error)) return false;
  return (
    error.message.includes("Importing a module script failed") ||
    error.message.includes("Failed to fetch dynamically imported module") ||
    error.message.includes("error loading dynamically imported module") ||
    error.name === "ChunkLoadError"
  );
}

export class ChunkErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, isChunkError: false, shouldAutoReload: false };
  }

  static getDerivedStateFromError(error: unknown): State {
    const isChunk = isChunkLoadError(error);
    const alreadyReloaded = sessionStorage.getItem("chunk_error_reload") === "1";
    return {
      hasError: true,
      isChunkError: isChunk,
      // Only auto-reload once — if we already tried, show the recovery UI instead
      shouldAutoReload: isChunk && !alreadyReloaded,
    };
  }

  componentDidCatch() {
    if (this.state.shouldAutoReload) {
      sessionStorage.setItem("chunk_error_reload", "1");
      window.location.reload();
    }
  }

  render() {
    if (!this.state.hasError) return this.props.children;

    // Auto-reload is about to fire — render nothing while the page reloads
    if (this.state.shouldAutoReload) return null;

    // Reload didn't fix it — likely a genuine network problem
    if (this.state.isChunkError) {
      return (
        <Box sx={{ p: 4, textAlign: "center" }}>
          <Typography variant="h6">Having trouble loading</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Check your connection and try again.
          </Typography>
          <Button
            onClick={() => {
              sessionStorage.removeItem("chunk_error_reload");
              window.location.reload();
            }}
            sx={{ mt: 2 }}
          >
            Try again
          </Button>
        </Box>
      );
    }

    // Something else went wrong — generic fallback
    return (
      <Box sx={{ p: 4, textAlign: "center" }}>
        <Typography variant="h6">Something went wrong</Typography>
        <Button onClick={() => window.location.reload()} sx={{ mt: 2 }}>
          Reload
        </Button>
      </Box>
    );
  }
}
